import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full font-semibold text-lg transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground active:bg-brand-30 disabled:bg-neutral-50 disabled:text-neutral-40",
        secondary:
          "bg-brand-60 text-primary-foreground active:bg-brand-50 disabled:bg-neutral-50 disabled:text-neutral-40",
        tertiary:
          "bg-transparent border border-white text-white active:bg-white/20 disabled:border-neutral-50 disabled:text-neutral-40",
        ghost: "text-brand-40 active:text-brand-50 disabled:text-neutral-40",
        vip: "bg-gradient-to-l from-brown-70 to-brown-50 border border-yellow-30 text-yellow-30 active:text-brown-70 active:bg-gradient-to-t active:from-yellow-40 active:to-yellow-20 disabled:from-transparent disabled:to-transparent disabled:border-transparent disabled:bg-neutral-50 disabled:text-neutral-40",
        expressive:
          "bg-gradient-to-b from-primary border-y border-t-brand-30 border-b-brand-40 via-brand-40 to-brand-30 text-white text-shadow-lg active:border-t-brand-30 active:from-brand-30 disabled:text-shadow-none disabled:transition-none disabled:via-transparent disabled:from-transparent disabled:to-transparent disabled:border-transparent disabled:bg-neutral-50 disabled:text-neutral-40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-10  gap-1.5 font-semibold text-lg px-3 has-[>svg]:px-2.5",
        default: "h-12 px-4 py-2 has-[>svg]:px-3 ",
        lg: "h-14 px-6 font-semibold text-lg has-[>svg]:px-4",
        "icon-sm": "size-10",
        icon: "size-12",
        "icon-lg": "size-14",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
